import React, { useState, useMemo, useCallback } from "react";
import Fuse from "fuse.js";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { ExpansionTable } from "./expansion-table";
import { Lead } from "model/lead";
import { MarketReportSubscription } from "model/market-report-subscription";
import { getMarketReportSubscriptionsForLead } from "redux/selector";
import { LoadingIndicator } from "component/shared/loading-indicator";

interface Props {
	lead: Lead;
}

const MarketReportSubscriptionsPanel: React.FC<Props> = ({ lead }) => {
	// Redux selectors
	const subscriptions = useSelector((state: RootState) => 
		getMarketReportSubscriptionsForLead(state, lead.id)
	);
	const loading = useSelector((state: RootState) => state.marketReportSubscriptions.loading);

	const [term, setTerm] = useState<string>("");

	// Memoized Fuse instance
	const fuse = useMemo(() => {
		const options: Fuse.IFuseOptions<MarketReportSubscription> = {
			shouldSort: true,
			threshold: 0.5,
			location: 0,
			distance: 100,
			minMatchCharLength: 1,
			keys: [
				"report.market.name"
			]
		};
		return new Fuse(subscriptions, options);
	}, [subscriptions]);

	// Memoized filtered subscriptions
	const filteredSubscriptions = useMemo(() => {
		if (!term) {
			return subscriptions;
		}
		return fuse.search(term).map((result) => result.item);
	}, [subscriptions, fuse, term]);

	// Memoized sorted subscriptions
	const sortedSubscriptions = useMemo(() => {
		return filteredSubscriptions.sort((a: any, b: any) => 
			a?.report?.market?.name.localeCompare(b?.report?.market?.name)
		);
	}, [filteredSubscriptions]);

	// Event handlers
	const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setTerm(event.target.value || "");
	}, []);

	if (loading) {
		return (
			<Mui.Grid container justifyContent="center">
				<Mui.Grid item>
					<LoadingIndicator />
				</Mui.Grid>
			</Mui.Grid>
		);
	}

	if (!subscriptions || !subscriptions.length) {
		return (
			<Mui.Typography>
				No current reports. Add one to stay in touch.
			</Mui.Typography>
		);
	}

	return (
		<>
			<Mui.InputBase
				placeholder="Search for Subscription"
				fullWidth
				onChange={handleSearchChange}
				value={term}
			/>
			{sortedSubscriptions.map((subscription, index) => (
				<ExpansionTable
					subscription={subscription}
					key={index}
					index={index}
				/>
			))}
		</>
	);
};

export { MarketReportSubscriptionsPanel };
